import { FC, Key, useEffect, useState } from 'react'
import {
  DomainSelector,
  Input,
  Spin,
  useDebounceState,
  useLocalStorage,
  useGlobalState,
  useMessage,
} from '@pepper/ui'
import { Icon } from '@iconify/react'
import clsx from 'clsx'
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params'
import { show as showModal } from '@ebay/nice-modal-react'

import { useContentRefreshParams } from '../hooks/useContentRefreshParams'
import { useSelectedCompanyId } from 'utils/functions/misc'
import { formatLongNumber } from 'utils/functions/formatLongNumber'
import Table from './Table'

import { useGetDomainRefreshParams } from 'container/SEOTools/hooks/useGetDomainRefreshParams'
import { useDeleteRefreshOpportunity } from 'container/SEOTools/hooks/useDeleteRefreshOpportunity'
import Banner from '../Banner'
import { useGetDomainRefreshParamsPaginated } from 'container/SEOTools/hooks/useGetDomainRefreshParamsPaginated'
import { useGenerateReport } from 'container/SEOTools/hooks/useGenerateReport'
import BottomPopup, { BottomPopupBtn } from 'components/BottomPopup'

import { RefreshablePagesList } from '../types'
import { getRefreshOpportunitiesColumn } from './Table/columns'
import QuickFilters from './QuickFilters'
import ReportMessage from './ReportMessage'

import s from './DomainRefresh.module.css'

const LIMIT = 20
const DEBOUNCE_TIME = 800

export const DomainRefresh: FC = () => {
  const { showError, showSuccess } = useMessage()
  const {
    domains,
    isFetching: isFetchingDomainsData,
    isLoading: isLoadingDomains,
  } = useContentRefreshParams()

  const [query, setQuery] = useQueryParams({
    search: StringParam,
    type: StringParam,
    page: withDefault(NumberParam, 1),
  })
  const companyId = useSelectedCompanyId()
  const { domainId, subscription, trialExhaust, permissions } = useGlobalState()
  const [lsDomainId] = useLocalStorage('domain_id', '')
  const selectedDomainId = (domainId ?? lsDomainId) || domains?.[0]?.id
  const selectedDomain = domains?.find(item => item.id === selectedDomainId)
  const isDomainConnectedToGsc = !!selectedDomain?.gsc_domain_property
  const {
    data: domainRefreshData,
    isFetching,
    isLoading,
  } = useGetDomainRefreshParams({
    companyId,
    domainId: selectedDomainId,
    isEnabled:
      companyId.length > 0 && isDomainConnectedToGsc && !!selectedDomainId,
  })
  const [selectedRows, setSelectedRows] = useState<React.Key[]>([])
  const [selectedRecords, setSelectedRecords] = useState<
    RefreshablePagesList[]
  >([])
  const [urlSearch, setUrlSearch] = useState('')
  const [isPageLoading, setIsPageLoading] = useState(true)
  const [isReportMsgVisibile, setIsReportMsgVisibile] = useState(false)
  const debouncedSearchValue = useDebounceState<string>(
    urlSearch,
    DEBOUNCE_TIME
  )

  const {
    data: domainRefreshDataPaginated,
    isFetching: isFetchingPaginatedPages,
    refetch,
  } = useGetDomainRefreshParamsPaginated({
    companyId,
    reportId: domainRefreshData?.id,
    isEnabled:
      !!companyId &&
      !!isDomainConnectedToGsc &&
      domainRefreshData?.id !== undefined,
    limit: LIMIT,
    queryParams: query,
  })

  const { mutateAsync: handleDelete, isLoading: isDeleting } =
    useDeleteRefreshOpportunity()

  const { mutateAsync: generateReport, isLoading: isGeneratingReports } =
    useGenerateReport()

  const [generatingIds, setGeneratingIds] = useState<string[]>([])
  const cmpAllowed = permissions?.cmp

  const activeDomainName = selectedDomain?.domain_url

  const handleDeleteReport = async (ids: Key[]) => {
    const response = await handleDelete({
      companyId,
      pageIds: ids.join(','),
    })

    if (response?.error) {
      showError(response.error)
    } else {
      showSuccess('Selected page(s) has been dismissed')
      setSelectedRows([])
    }
  }

  const handleUrlSearch = (event: { target: { value: string } }) => {
    const searchValue = event.target.value
    setUrlSearch(searchValue)
    setQuery({ page: 1 })
  }

  const handleGenerateReport = async (
    records: RefreshablePagesList[],
    callback?: () => void
  ) => {
    const isTrialExpired = subscription?.trialStatus === 'expired'
    const isLimitExhausted = trialExhaust?.seoContentRefresh

    if (!cmpAllowed || isTrialExpired || isLimitExhausted) {
      showModal('buy-cmp', {
        ...(isLimitExhausted && {
          id: 'seoContentRefresh',
        }),
      })
      return
    }

    setGeneratingIds(records.map(r => r.id))

    if (!selectedDomain) {
      showError('Invalid domain selected')
    } else {
      const region_code = selectedDomain.country_code
      const payload = records.map(r => ({
        page_url: r.page_url,
        region_code,
        refreshable_page_id: r.id,
      }))
      const response = await generateReport({
        payload,
        companyId,
      })

      if (response?.error) {
        showError(response.error)
      } else {
        setIsReportMsgVisibile(true)
        setSelectedRows([])
        callback?.()
      }
    }
  }

  const handlePageChange = (pageNumber: number) => {
    setQuery({ page: pageNumber })
    setSelectedRecords([])
    setSelectedRows([])
  }

  useEffect(() => {
    const loading =
      isFetching || isFetchingPaginatedPages || isFetchingDomainsData
    setIsPageLoading(loading)
  }, [isFetching, isFetchingPaginatedPages, isFetchingDomainsData])

  useEffect(() => {
    setQuery({
      search: debouncedSearchValue || undefined,
    })
  }, [debouncedSearchValue])

  return (
    <Spin spinning={isPageLoading || isLoadingDomains}>
      <div className={s.domainSelectorAndStats}>
        <DomainSelector
          companyId={companyId}
          size="regular"
          isControlled={false}
        />
        {isDomainConnectedToGsc &&
        domainRefreshData?.traffic &&
        domainRefreshData?.potential_traffic ? (
          <div
            className={s.statsWrapper}
            data-id="refresh-current-potential-traffic"
          >
            <div className={clsx(s.stats, s.currenTraffic)}>
              <span className={s.statsCount}>
                {formatLongNumber(domainRefreshData.traffic, 1)}
              </span>{' '}
              Your current traffic
            </div>
            <div className={clsx(s.stats, s.potentialTraffic)}>
              <span className={s.statsCount}>
                {formatLongNumber(domainRefreshData.potential_traffic, 1)}
              </span>
              <Icon icon="lucide:arrow-up-right" height={20} />
              &nbsp;Your potential traffic
            </div>
          </div>
        ) : null}
      </div>
      {isReportMsgVisibile && (
        <ReportMessage onClose={() => setIsReportMsgVisibile(false)} />
      )}
      {!isDomainConnectedToGsc && !isLoadingDomains && (
        <Banner tab="DOMAIN_REFRESH" handleRefetch={refetch} />
      )}
      {isDomainConnectedToGsc &&
        domainRefreshData &&
        domainRefreshDataPaginated && (
          <>
            <div className={s.header}>
              <QuickFilters stats={domainRefreshData} />
              <div>
                <Input
                  type="search"
                  placeholder="Search a URL"
                  className={s.searchInput}
                  prefix={
                    <Icon icon="lucide:search" className={s.searchIcon} />
                  }
                  onChange={handleUrlSearch}
                  value={urlSearch ?? query.search}
                />
              </div>
            </div>
            <div className={s.table}>
              <Table
                columns={getRefreshOpportunitiesColumn({
                  domainName: activeDomainName,
                  handleDelete: reportId => handleDeleteReport([reportId]),
                  handleGenerateGapReport: handleGenerateReport,
                  isGeneratingReports,
                  totalRecords: domainRefreshDataPaginated.total_records,
                  generatingIds,
                  selectedRowKeys: selectedRows,
                })}
                dataSource={domainRefreshDataPaginated.data}
                onPageChange={handlePageChange}
                totalRecords={domainRefreshDataPaginated.total_records}
                pageSize={LIMIT}
                page={query.page}
                selectedRowKeys={selectedRows}
                onRowSelect={(ids, records) => {
                  setSelectedRows(ids)
                  setSelectedRecords(records)
                }}
              />
            </div>
            <BottomPopup
              selectedCount={selectedRows.length}
              onClose={() => setSelectedRows([])}
            >
              <BottomPopupBtn
                icon={<Icon icon="lucide:arrow-right" />}
                onClick={() => {
                  handleGenerateReport(selectedRecords, () =>
                    setSelectedRecords([])
                  )
                }}
              >
                Generate Report
              </BottomPopupBtn>
              <BottomPopupBtn
                icon={<Icon icon="gg:remove" />}
                disabled={isDeleting}
                onClick={() => handleDeleteReport(selectedRows)}
              >
                Dismiss
              </BottomPopupBtn>
            </BottomPopup>
          </>
        )}
      {!(
        isDomainConnectedToGsc &&
        domainRefreshData &&
        domainRefreshDataPaginated
      ) &&
        !isLoading && <div className={s.noResults}>No results found.</div>}
    </Spin>
  )
}
